import { type DateFilter, DAY_FORMAT, dateRangeOf, datePresets } from '@lib/inspections/filters';
import { useLocalization } from '@lib/localization';
import { Box, Button, ListItemButton, ListItemText, Popover, Tooltip, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { deDE, enUS, frFR, itIT, jaJP, trTR } from '@mui/x-date-pickers/locales';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { brand, gray } from '@theme/primitives';
import dayjs, { type Dayjs } from 'dayjs';
import { CalendarDays, Check, ChevronDown } from 'lucide-react';
import { createContext, useContext, useId, useState } from 'react';
import { pillStyles, pillValueStyles, popoverFooterStyles, popoverPaperStyles } from './filters.styles';

interface DateRangeFilterProps {
  value: DateFilter | null;
  onChange: (value: DateFilter | null) => void;
}

/**
 * The picker's own words — the arrows' and the view switch's labels a screen reader reads —
 * for the interface languages that have them. The day and month names come from dayjs.
 */
const pickerLocales: Record<string, typeof enUS> = { de: deDE, en: enUS, fr: frFR, it: itIT, ja: jaJP, tr: trTR };

const pickerLocaleText = (language: string) =>
  (pickerLocales[language.split('-')[0]] ?? enUS).components.MuiLocalizationProvider.defaultProps.localeText;

/** "9 – 16 Oct 2026", collapsed the way the language writes a span of days. */
const formatRange = (language: string, from: Dayjs, to: Dayjs): string =>
  new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric' })
    .formatRange(from.toDate(), to.toDate());

/**
 * What a day of the calendar needs to know to draw the range: the chosen ends, and while the
 * second end is being chosen, the day under the pointer or the keyboard, so the span shows
 * before it is committed. Through context rather than the day slot's props, which are typed
 * to the picker's own and would need a cast to carry these.
 */
interface RangeDrawing {
  start: Dayjs | null;
  end: Dayjs | null;
  hovered: Dayjs | null;
  setHovered: (day: Dayjs | null) => void;
}

const RangeDrawingContext = createContext<RangeDrawing>({ start: null, end: null, hovered: null, setHovered: () => undefined });

/** Same width as a weekday label above it (36 plus the 2 the picker puts either side), so the band runs unbroken under the columns. */
const CELL_WIDTH = 40;
const CELL_HEIGHT = 36;
const BAND = alpha(brand[400], 0.16);
const PREVIEW_BAND = alpha(brand[400], 0.08);

interface BandProps {
  band: 'none' | 'full' | 'left' | 'right';
  preview: boolean;
  roundLeft: boolean;
  roundRight: boolean;
}

/**
 * The strip a range lays across a row. An end of the range is half a strip, towards the other
 * end, under its circle; a strip is rounded where a week or the month breaks it, since the
 * days outside the month are not drawn.
 */
const DayBand = styled('div', {
  shouldForwardProp: (prop) => !['band', 'preview', 'roundLeft', 'roundRight'].includes(prop as string),
})<BandProps>(({ band, preview, roundLeft, roundRight }) => {
  const color = preview ? PREVIEW_BAND : BAND;
  const background = {
    none: 'transparent',
    full: color,
    left: `linear-gradient(to right, ${color} 50%, transparent 50%)`,
    right: `linear-gradient(to right, transparent 50%, ${color} 50%)`,
  }[band];

  return {
    width: CELL_WIDTH,
    height: CELL_HEIGHT,
    display: 'flex',
    justifyContent: 'center',
    background,
    borderTopLeftRadius: roundLeft ? CELL_HEIGHT / 2 : 0,
    borderBottomLeftRadius: roundLeft ? CELL_HEIGHT / 2 : 0,
    borderTopRightRadius: roundRight ? CELL_HEIGHT / 2 : 0,
    borderBottomRightRadius: roundRight ? CELL_HEIGHT / 2 : 0,
  };
});

/** The first day the calendar offers. Without a bound its year view listed every year from 1900. */
const EARLIEST_DAY = dayjs('2020-01-01');

/** Orders two days, so a range chosen from its end backwards is the same range. */
const ordered = (a: Dayjs, b: Dayjs): [Dayjs, Dayjs] => (a.isAfter(b, 'day') ? [b, a] : [a, b]);

const RangeDay: React.FC<PickersDayProps> = (props) => {
  const { day: pickerDay, outsideCurrentMonth, onMouseEnter, onFocus, ...other } = props;
  const { start, end, hovered, setHovered } = useContext(RangeDrawingContext);
  const day = pickerDay as Dayjs;

  // While the second end is being chosen, the span runs to wherever the reader points.
  const preview = Boolean(start && !end && hovered);
  const span = start && (end ?? (preview ? hovered : null)) ? ordered(start, (end ?? hovered)!) : null;

  const isFrom = Boolean(span && day.isSame(span[0], 'day'));
  const isTo = Boolean(span && day.isSame(span[1], 'day'));
  const inside = Boolean(span && day.isAfter(span[0], 'day') && day.isBefore(span[1], 'day'));
  const isChosen = Boolean(start && day.isSame(start, 'day')) || Boolean(end && day.isSame(end, 'day'));

  let band: BandProps['band'] = 'none';

  if (!outsideCurrentMonth && span && !span[0].isSame(span[1], 'day')) {
    if (inside) band = 'full';
    else if (isFrom) band = 'right';
    else if (isTo) band = 'left';
  }

  // Locale-aware: dayjs starts the week where the reader's language does, as the picker does.
  const rowStart = day.isSame(day.startOf('week'), 'day') || day.date() === 1;
  const rowEnd = day.isSame(day.endOf('week'), 'day') || day.isSame(day.endOf('month'), 'day');

  return (
    <DayBand band={band} preview={preview} roundLeft={band === 'full' && rowStart} roundRight={band === 'full' && rowEnd}>
      <PickersDay
        {...other}
        // The calendar's own, in `other`, is false for every day: its value is always empty,
        // since the range is drawn here. The days of the range in force are selected, and
        // while the second end is being picked only the first.
        aria-selected={!outsideCurrentMonth && (isChosen || (!preview && (isFrom || isTo || inside)))}
        day={pickerDay}
        outsideCurrentMonth={outsideCurrentMonth}
        disableMargin
        // The end under the pointer is not chosen yet, so it is drawn lighter than one that is.
        selected={!outsideCurrentMonth && (isChosen || (!preview && (isFrom || isTo)))}
        onMouseEnter={(event, value) => {
          setHovered(value as Dayjs);
          onMouseEnter?.(event, value);
        }}
        onFocus={(event, value) => {
          setHovered(value as Dayjs);
          onFocus?.(event, value);
        }}
        sx={{
          // A day inside the range keeps the band's colour under the hover rather than a grey disc.
          ...(inside && !outsideCurrentMonth && { '&:hover': { backgroundColor: alpha(brand[400], 0.24) } }),
          ...(preview && (isFrom || isTo) && !isChosen && {
            '&, &:hover, &:focus': { backgroundColor: alpha(brand[400], 0.45), color: 'common.white' },
          }),
        }}
      />
    </DayBand>
  );
};

/**
 * When the inspections were last updated, as a pill that opens onto the dashboard's presets
 * and a calendar. A preset applies at once. On the calendar the first day chosen is one end
 * and the second the other, in either order, and the range applies the moment it has both;
 * the same day twice is a range of one day. Closing with only one end chosen changes nothing.
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ value, onChange }) => {
  const { t, currentLanguage } = useLocalization();
  const popoverId = useId();

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // The ends of a range being chosen on the calendar; empty until a day is clicked.
  const [draftStart, setDraftStart] = useState<Dayjs | null>(null);
  const [hovered, setHovered] = useState<Dayjs | null>(null);
  // The month the calendar opens on, taken once per opening: the picker moves its month
  // whenever this changes, so one made anew on every render would hold it in place.
  const [openedOn, setOpenedOn] = useState<Dayjs>(() => dayjs());
  // The last day it offers, taken once per opening as `openedOn` is: a new `maxDate` on every
  // render would move the picker. The year view then lists 2020 to this year, not 1900–2099.
  const [today, setToday] = useState<Dayjs>(() => dayjs());

  const range = value && dateRangeOf(value);
  const activePreset = value?.kind === 'preset' ? datePresets.find((preset) => preset.id === value.preset) : undefined;

  const close = () => {
    setAnchor(null);
    setDraftStart(null);
    setHovered(null);
  };

  const apply = (next: DateFilter | null) => {
    onChange(next);
    close();
  };

  const pickDay = (day: Dayjs) => {
    if (!draftStart) {
      setDraftStart(day);
      return;
    }

    const [from, to] = ordered(draftStart, day);

    apply({ kind: 'custom', from: from.format(DAY_FORMAT), to: to.format(DAY_FORMAT) });
  };

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setOpenedOn(range?.to ?? dayjs());
    setToday(dayjs());
    setAnchor(event.currentTarget);
  };

  // While a range is being chosen the calendar shows that; otherwise the range in force.
  const drawing: RangeDrawing = draftStart
    ? { start: draftStart, end: null, hovered, setHovered }
    : { start: range?.from ?? null, end: range?.to ?? null, hovered: null, setHovered };

  const label = range
    ? (activePreset ? t(activePreset.label) : formatRange(currentLanguage, range.from, range.to))
    : null;

  const footer = draftStart
    ? t('Inspections_Filter_PickEnd')
    : range ? formatRange(currentLanguage, range.from, range.to) : t('Inspections_Filter_PickStart');

  return (
    <>
      {/* Set, the pill shows the range in place of its name rather than beside it: the two
          side by side made it the widest thing in the toolbar, and a toolbar that grew broke
          onto a second line and pushed the list down. The name stays in the tooltip. */}
      <Tooltip title={label ? t('Common_DateUpdated') : ''}>
        <Button
          variant="outlined"
          size="small"
          onClick={open}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          aria-controls={anchor ? popoverId : undefined}
          aria-label={label ? `${t('Common_DateUpdated')}: ${label}` : undefined}
          sx={pillStyles(Boolean(value))}
        >
          <CalendarDays size={15} />
          {label
            ? <Box component="span" sx={{ ...pillValueStyles, maxWidth: 160 }}>{label}</Box>
            : <span>{t('Common_DateUpdated')}</span>}
          <ChevronDown size={14} style={{ marginLeft: -2 }} />
        </Button>
      </Tooltip>

      <Popover
        id={popoverId}
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: popoverPaperStyles, role: 'dialog', 'aria-label': t('Common_DateUpdated') } }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box
            component="ul"
            sx={{
              listStyle: 'none', m: 0, p: 1, display: 'flex', gap: 0.25,
              flexDirection: { xs: 'row', sm: 'column' }, flexWrap: { xs: 'wrap', sm: 'nowrap' },
              minWidth: { sm: 168 },
              borderRight: { sm: (theme) => `1px solid ${theme.palette.divider}` },
              borderBottom: { xs: (theme) => `1px solid ${theme.palette.divider}`, sm: 'none' },
            }}
          >
            {datePresets.map((preset) => {
              const selected = activePreset?.id === preset.id;

              return (
                <li key={preset.id}>
                  <ListItemButton
                    selected={selected}
                    onClick={() => apply({ kind: 'preset', preset: preset.id })}
                    sx={{
                      borderRadius: 1, py: 0.75, px: 1.25, gap: 1,
                      '&.Mui-selected': { backgroundColor: alpha(brand[400], 0.16) },
                      '&.Mui-selected:hover': { backgroundColor: alpha(brand[400], 0.24) },
                    }}
                  >
                    <ListItemText
                      primary={t(preset.label)}
                      slotProps={{ primary: { sx: { fontSize: 13.5, color: selected ? brand[100] : 'text.primary' } } }}
                    />
                    {selected && <Check size={14} color={brand[300]} />}
                  </ListItemButton>
                </li>
              );
            })}
          </Box>

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={currentLanguage}
            localeText={pickerLocaleText(currentLanguage)}
          >
            <RangeDrawingContext.Provider value={drawing}>
              <Box onMouseLeave={() => setHovered(null)}>
                <DateCalendar
                  value={null}
                  // Year first is one click away from the header; the calendar opens on the month in force.
                  views={['year', 'day']}
                  openTo="day"
                  referenceDate={openedOn}
                  minDate={EARLIEST_DAY}
                  maxDate={today}
                  disableFuture
                  onChange={(day, selectionState) => {
                    // Choosing a year only moves the calendar to it; a range is made of days.
                    if (day && selectionState === 'finish') pickDay(day as Dayjs);
                  }}
                  slots={{ day: RangeDay }}
                  sx={{
                    width: 300,
                    height: 'auto',
                    maxHeight: 336,
                    '& .MuiDayCalendar-weekDayLabel': { color: gray[400], width: CELL_WIDTH, m: 0 },
                    '& .MuiDayCalendar-weekContainer': { m: '2px 0', justifyContent: 'center' },
                    '& .MuiDayCalendar-header': { justifyContent: 'center' },
                    // The year view is 320 wide by itself, which cut its third column.
                    '& .MuiYearCalendar-root': { width: '100%' },
                    // A year is a plain `button`, and index.css pads every button: the year stood
                    // off to the right and below the circle that hover and focus draw.
                    '& .MuiYearCalendar-button': { padding: 0 },
                    '& .MuiPickersCalendarHeader-root': { pl: 2, pr: 1 },
                    '& .MuiPickersCalendarHeader-label': { fontSize: 14, fontWeight: 500 },
                    '& .MuiPickersArrowSwitch-button, & .MuiPickersCalendarHeader-switchViewButton': {
                      width: 30, height: 30, border: 'none', backgroundColor: 'transparent',
                      '&:hover': { backgroundColor: gray[800] },
                    },
                  }}
                />
              </Box>
            </RangeDrawingContext.Provider>
          </LocalizationProvider>
        </Box>

        <Box sx={popoverFooterStyles}>
          <Typography variant="caption" color={draftStart ? 'primary.light' : 'text.secondary'} aria-live="polite">
            {footer}
          </Typography>
          <Button size="small" variant="text" disabled={!value} onClick={() => apply(null)} sx={{ height: 28, px: 1 }}>
            {t('Inspections_Filter_ClearSelection')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeFilter;
